import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Paper, Typography, CircularProgress, Button, Divider, Chip, Card, CardContent, Avatar
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
    CheckCircle, Block, Star, Person, Phone, Email, DriveEta,
    AccountBalanceWallet, Loyalty, CalendarToday, MonetizationOn
} from '@mui/icons-material';
import { getDriverDetails } from '../services/driverService';
import { getWallet } from '../services/walletService'; // Make sure this is imported

// Define a robust interface for the Driver data
interface DriverDetailType {
    id: number;
    userUid: string;
    licenseNumber?: string;
    vehicleDetails?: string;
    carName?: string;
    vehicleType?: string;
    vehicleColor?: string;
    vehicleRcDetails?: string;
    vehicleLicensePlateNumber?: string;
    isAvailable: boolean;
    rating: number;
    isDeleted: boolean;
    createdAt?: string;
    updatedAt?: string;
    approvedAt?: string;
    cohortWeek?: string;
    isRCAvailable: boolean;
    isRCVerified: boolean;
    isDLAvailable: boolean;
    isDLVerified: boolean;
    isProfilePhotoAvailable: boolean;
    isProfilePhotoVerified: boolean;
    profilePhotoUrl?: string;
    status: string;
    // Relations
    user?: {
        id: number;
        name?: string;
        phone?: string;
        email?: string;
        firebaseUid: string;
    };
    retentionProgress?: {
        weeksCompleted: number;
        totalBonusPaid: number;
        nextPayDate: string | null;
    };
}

const DriverDetail: React.FC = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [driver, setDriver] = useState<DriverDetailType | null>(null);
    const [walletBalance, setWalletBalance] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDriver = async () => {
            if (!id) return;
            try {
                const res = await getDriverDetails(Number(id));
                setDriver(res.data);

                // Fetch Wallet Separately to show live balance
                if (res.data.userUid) {
                    try {
                        const wRes = await getWallet({ driverUid: res.data.userUid });
                        // @ts-ignore - Assuming API structure matches response { data: { totalBalance... } }
                        setWalletBalance(wRes.data?.data?.totalBalance || 0);
                    } catch (e) {
                        console.warn("Wallet fetch failed", e);
                    }
                }

            } catch (err) {
                console.error("Failed to fetch driver details", err);
            } finally {
                setLoading(false);
            }
        };
        fetchDriver();
    }, [id]);

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}><CircularProgress /></Box>;
    if (!driver) return <Box sx={{ p: 3 }}><Typography>Driver not found</Typography></Box>;

    const InfoRow = ({ label, value, icon }: { label: string, value: any, icon?: React.ReactNode }) => (
        <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                {icon}
                <Typography variant="subtitle2" color="textSecondary" sx={{ fontWeight: 500 }}>
                    {label}
                </Typography>
            </Box>
            <Typography variant="body1" sx={{ fontWeight: 400 }}>
                {value || <span style={{ color: '#aaa' }}>-</span>}
            </Typography>
        </Box>
    );

    return (
        <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
            {/* Header Navigation */}
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/drivers')}
                sx={{ mb: 2, textTransform: 'none' }}
            >
                Back to Driver List
            </Button>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                    Driver Details <Chip label={`#${driver.id}`} size="small" sx={{ ml: 1, verticalAlign: 'middle' }} />
                </Typography>
                <Chip
                    label={driver.status || "OFFLINE"}
                    color={driver.status === 'ONLINE' ? 'success' : 'default'}
                    variant="filled"
                />
            </Box>

            {/* Main Layout - Switched to md breakpoint for wider support */}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>

                {/* LEFT COLUMN: Main Driver Info (60% Width) */}
                <Box sx={{ flex: { md: 3 }, minWidth: 0 }}>

                    {/* 1. Vehicle & License Section */}
                    <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DriveEta color="primary" />
                            Vehicle & License Information
                        </Typography>
                        <Divider sx={{ mb: 3 }} />

                        {/* Flexbox Layout for 2 Columns */}
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            <Box sx={{ flex: '1 1 300px' }}>
                                <InfoRow label="Car Name" value={driver.carName} />
                                <InfoRow label="Vehicle Type" value={driver.vehicleType} />
                                <InfoRow label="License Plate" value={driver.vehicleLicensePlateNumber} />
                                <InfoRow label="Vehicle Color" value={driver.vehicleColor} />
                            </Box>
                            <Box sx={{ flex: '1 1 300px' }}>
                                <InfoRow label="License Number" value={driver.licenseNumber} />
                                <InfoRow label="Vehicle Details" value={driver.vehicleDetails} />
                                <InfoRow label="Rating" value={
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <Typography fontWeight="bold">{driver.rating?.toFixed(1) || "N/A"}</Typography>
                                        <Star fontSize="small" color="warning" />
                                    </Box>
                                } />
                                <InfoRow label="Driver Created At" value={driver.createdAt ? new Date(driver.createdAt).toLocaleDateString() : '-'} />
                            </Box>
                        </Box>
                    </Paper>

                    {/* 2. Verification Status Section */}
                    <Paper sx={{ p: 3, borderRadius: 2 }}>
                        <Typography variant="h6" gutterBottom>Verification Status</Typography>
                        <Divider sx={{ mb: 3 }} />

                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                            <Chip
                                icon={driver.isAvailable ? <CheckCircle /> : <Block />}
                                label={driver.isAvailable ? "Availability: ON" : "Availability: OFF"}
                                color={driver.isAvailable ? "success" : "default"}
                                sx={{ justifyContent: 'flex-start', py: 2.5, px: 1, fontSize: '0.9rem', flex: '1 0 auto' }}
                            />
                            <Chip
                                icon={driver.isRCVerified ? <CheckCircle /> : <Block />}
                                label="RC Verified"
                                color={driver.isRCVerified ? "success" : "error"}
                                variant="outlined"
                                sx={{ justifyContent: 'flex-start', py: 2.5, px: 1, fontSize: '0.9rem', flex: '1 0 auto' }}
                            />
                            <Chip
                                icon={driver.isDLVerified ? <CheckCircle /> : <Block />}
                                label="DL Verified"
                                color={driver.isDLVerified ? "success" : "error"}
                                variant="outlined"
                                sx={{ justifyContent: 'flex-start', py: 2.5, px: 1, fontSize: '0.9rem', flex: '1 0 auto' }}
                            />
                            <Chip
                                icon={driver.isProfilePhotoVerified ? <CheckCircle /> : <Block />}
                                label="Photo Verified"
                                color={driver.isProfilePhotoVerified ? "success" : "error"}
                                variant="outlined"
                                sx={{ justifyContent: 'flex-start', py: 2.5, px: 1, fontSize: '0.9rem', flex: '1 0 auto' }}
                            />
                        </Box>
                    </Paper>
                </Box>

                {/* RIGHT COLUMN: Business & User Info (40% Width) */}
                <Box sx={{ flex: { md: 2 }, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>

                    {/* NEW: Financial & Business Plan Card */}
                    <Card variant="outlined" sx={{ borderRadius: 2, borderTop: '4px solid #2e7d32', bgcolor: '#f9fcf9' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <MonetizationOn color="success" />
                                <Typography variant="h6">Business & Finance</Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            {/* Flexbox Layout for Finance */}
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                {/* Row 1: Wallet & Cohort */}
                                <Box sx={{ flex: '1 1 45%' }}>
                                    <InfoRow
                                        label="Wallet Balance"
                                        value={
                                            <span style={{
                                                color: (walletBalance || 0) < -500 ? 'red' : 'green',
                                                fontWeight: 'bold'
                                            }}>
                                                {walletBalance !== null ? `₹${walletBalance}` : 'Loading...'}
                                            </span>
                                        }
                                        icon={<AccountBalanceWallet fontSize="small" color="action"/>}
                                    />
                                </Box>
                                <Box sx={{ flex: '1 1 45%' }}>
                                    <InfoRow
                                        label="Cohort Batch"
                                        value={<Chip size="small" label={driver.cohortWeek || "None"} />}
                                        icon={<CalendarToday fontSize="small" color="action"/>}
                                    />
                                </Box>

                                {/* Row 2: Retention & Status */}
                                <Box sx={{ flex: '1 1 100%' }}>
                                    <Box sx={{ mb: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                            <Loyalty fontSize="small" color="action"/>
                                            <Typography variant="subtitle2" color="textSecondary">Retention Progress</Typography>
                                        </Box>
                                        {driver.retentionProgress ? (
                                            <Box>
                                                <Typography variant="body2">
                                                    Week {driver.retentionProgress.weeksCompleted + 1}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary">
                                                    Paid So Far: ₹{driver.retentionProgress.totalBonusPaid}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Typography variant="body2" color="textSecondary">Not Enrolled</Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                            <Button
                                fullWidth
                                variant="outlined"
                                color="success"
                                size="small"
                                onClick={() => navigate(`/wallet-transactions?driverUid=${driver.userUid}`)}
                                sx={{ mt: 1 }}
                            >
                                View Wallet Transactions
                            </Button>
                        </CardContent>
                    </Card>

                    {/* Linked User Card */}
                    <Card variant="outlined" sx={{ borderRadius: 2, borderTop: '4px solid #1976d2' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                <Person color="primary" />
                                <Typography variant="h6">Linked User</Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />

                            {driver.user ? (
                                <>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                        <Avatar sx={{ bgcolor: '#1976d2', width: 50, height: 50 }}>
                                            {driver.user.name ? driver.user.name.charAt(0).toUpperCase() : 'U'}
                                        </Avatar>
                                        <Box sx={{ overflow: 'hidden' }}>
                                            <Typography variant="subtitle1" fontWeight="bold" noWrap>
                                                {driver.user.name || "No Name"}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary" sx={{ fontFamily: 'monospace', display: 'block' }} noWrap>
                                                ID: {driver.user.id}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    <InfoRow label="Phone" value={driver.user.phone} icon={<Phone fontSize="small" color="action"/>} />
                                    <InfoRow label="Email" value={driver.user.email} icon={<Email fontSize="small" color="action"/>} />

                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        onClick={() => navigate(`/user/${driver.user?.id}`)}
                                        sx={{ mt: 1 }}
                                    >
                                        View Full User Profile
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <InfoRow label="User UID" value={driver.userUid} />
                                    <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                                        Full user profile data not loaded.
                                    </Typography>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        onClick={() => navigate(`/users?search=${driver.userUid}`)}
                                    >
                                        Find User by UID
                                    </Button>
                                </>
                            )}
                        </CardContent>
                    </Card>

                    {/* Activity Card */}
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>Activity Shortcuts</Typography>
                            <Divider sx={{ mb: 2 }} />
                            <Button
                                fullWidth
                                variant="contained"
                                sx={{ mb: 1.5 }}
                                onClick={() => navigate(`/rides?driverUid=${driver.userUid}`)}
                            >
                                View Ride History
                            </Button>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => navigate(`/wallet-transactions?driverUid=${driver.userUid}`)}
                            >
                                View Wallet Details
                            </Button>
                        </CardContent>
                    </Card>
                </Box>
            </Box>
        </Box>
    );
};

export default DriverDetail;